/**
 * The chat-completions call shared by OpenRouter and xAI: both speak OpenAI's
 * request shape and SSE stream. (Ollama uses its native API; see `local.ts`.)
 */

import type { ShaderChatMessage } from '../types'
import { ShaderGenerationError } from '../types'
import { abortedError, httpError, toShaderError } from './errors'
import { readChatCompletionStream } from './sse'

/** What every transport receives. */
export interface ChatTransportRequest {
  model: string
  system: string
  messages: ShaderChatMessage[]
  signal: AbortSignal
  onText?: (textSoFar: string) => void
  temperature: number
  maxTokens: number
}

/** A model the user can pick. */
export interface ShaderModelOption {
  id: string
  label: string
}

export interface ChatCompletionTarget {
  url: string
  /** Names the provider in error messages. */
  label: string
  headers?: Record<string, string>
  /** Redacted from any error text. */
  secret?: string
  /** Explains a fetch `TypeError` (host unreachable or blocked by CORS). */
  onNetworkError?: () => ShaderGenerationError
}

export async function postChatCompletion(target: ChatCompletionTarget, request: ChatTransportRequest): Promise<string> {
  const { signal } = request
  if (signal.aborted) throw abortedError()
  const secrets = [target.secret]
  try {
    const response = await fetch(target.url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...target.headers },
      body: JSON.stringify({
        model: request.model,
        messages: [{ role: 'system', content: request.system }, ...request.messages],
        stream: true,
        temperature: request.temperature,
        max_tokens: request.maxTokens,
      }),
      signal,
    })
    if (!response.ok) throw await httpError(response, target.label, secrets)
    return await readChatCompletionStream(response, signal, request.onText)
  } catch (error) {
    throw toShaderError(error, { secrets, onNetworkError: target.onNetworkError })
  }
}

/** GET a JSON listing; failures become generation errors like the chat call's. */
export async function getJson<T>(target: ChatCompletionTarget, signal?: AbortSignal): Promise<T> {
  try {
    const response = await fetch(target.url, { headers: target.headers, signal })
    if (!response.ok) throw await httpError(response, target.label, [target.secret])
    return (await response.json()) as T
  } catch (error) {
    throw toShaderError(error, { secrets: [target.secret], onNetworkError: target.onNetworkError })
  }
}
